import './styles.css';
import './hud.css';
import * as THREE from 'three';
import { World, emptyCommand } from './sim/world';
import { WEATHER_KINDS } from './sim/weather';
import { WorldView, QUALITY } from './render/view';
import { CAMERA_MODES, type CameraMode } from './render/cameras';
import { Effects } from './render/effects/effects';
import { Environment } from './render/environment';
import { LAYER_OPAQUE, LAYER_WATER } from './render/pipeline';
import { AudioEngine } from './audio/audio';
import { Input } from './ui/input';
import { Hud } from './ui/hud';
import { clamp } from './core/math';
import { FullscreenPass } from './render/gpu';
import { NOISE3D_FRAG } from './render/sky/cloudGlsl';
import { Art } from './render/ship/art';
import { SHIP_CLASSES } from './data/classes';
import { weatherTuning } from './render/materials/materials';

const canvas = document.getElementById('view') as HTMLCanvasElement;
const uiRoot = document.getElementById('ui')!;

const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance', stencil: false });
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.shadowMap.autoUpdate = false;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.NoToneMapping;

const params = new URLSearchParams(location.search);
const world = new World({
  enemies: params.get('enemies') !== '0',
  difficulty: (['easy', 'normal', 'hard', 'legendary'] as const).find((d) => d === params.get('difficulty')) ?? 'normal',
});
const audio = new AudioEngine();
const cmd = emptyCommand();
let view: WorldView;
let effects: Effects;
let env: Environment;
let input: Input;
let helm = 0;
let started = false;
let renderScale = 1;
let frameAvg = 1 / 60;
let scaleTimer = 0;
let fpsAvg = 60;

const hud = new Hud(uiRoot, world, {
  setWeather: (k) => world.weather.set(k, 25),
  setDynamicWeather: (on) => (world.weather.dynamic = on),
  setHours: (h) => world.day.setHours(h),
  setTimeScale: (s) => (world.day.timeScale = s),
  setGameSpeed: (s) => (world.speed = s),
  setWaveIntensity: (v) => (world.settings.waveIntensity = v),
  setWind: (s) => (world.settings.windSpeed = s),
  setQuality: (k) => {
    view.setQuality(QUALITY[k]!);
    renderScale = 1;
    afterResize();
  },
  setPost: (k, on) => (view.pipeline.settings[k] = on),
  setVolume: (v) => audio.setVolume(v),
  setCamera: (m) => view.rig.setMode(m, input),
  setSensitivity: (v) => (input.sensitivity = v),
  setEnemies: (on) => (world.encounters.enabled = on),
  setDifficulty: (d) => (world.encounters.difficulty = d),
  spawn: (kind) => world.spawn(kind, 2600),
  restart: () => location.reload(),
  // The battle view frames whatever ship we're keeping a glass on.
  track: (id) => {
    if (id >= 0) view.rig.targetId = id;
  },
  start: () => {
    started = true;
    audio.start();
    canvas.focus();
  },
});

function afterResize(): void {
  const p = view.pipeline;
  effects.setDepth(p.opaqueDepth, p.renderWidth, p.renderHeight, view.rig.camera.near, view.rig.camera.far);
}

function resize(): void {
  view.resize(window.innerWidth, window.innerHeight);
  view.setRenderScale(renderScale);
  afterResize();
}

async function init(): Promise<void> {
  // The Blender-built ships, rig, props and crew (?art=0 for the procedural ones).
  hud.setStatus('Loading models…', false);
  const art = params.get('art') === '0' ? null : await Art.load(import.meta.env.BASE_URL + 'models/', Object.keys(SHIP_CLASSES));
  view = new WorldView(renderer, world, art);
  env = new Environment(world, view.shipModel.materials);
  view.scene.add(env.group);
  effects = new Effects(view.scene, world, view.ocean.foam, view.ship);
  input = new Input(canvas);
  canvas.tabIndex = 0;
  canvas.addEventListener('click', () => {
    if (view.rig.mode === 'free' || view.rig.mode === 'deck') input.requestLock();
  });
  window.addEventListener('resize', resize);
  resize();
  // Warm up: one frame to create render targets, then compile every material.
  step(1 / 60, true);
  const cam = view.rig.camera;
  cam.layers.enable(LAYER_OPAQUE);
  cam.layers.enable(LAYER_WATER);
  hud.setStatus('Compiling shaders…', false);
  try {
    await renderer.compileAsync(view.scene, cam);
  } catch {
    // Not fatal: shaders compile on first use.
  }
  cam.layers.set(LAYER_OPAQUE);
  view.endWarmup();
  hud.setStatus('Ready', true);
  applyUrlOptions();
  last = performance.now();
  requestAnimationFrame(frame);
}

function handleKeys(dt: number): void {
  const mode = view.rig.mode;
  const walking = mode === 'free' || mode === 'deck';
  const steer = input.axis(walking ? ['ArrowLeft'] : ['ArrowLeft', 'KeyA'], walking ? ['ArrowRight'] : ['ArrowRight', 'KeyD']);
  if (steer !== 0) helm = clamp(helm + steer * 0.9 * dt, -1, 1);
  else helm -= Math.sign(helm) * Math.min(Math.abs(helm), 0.6 * dt);
  cmd.helm = helm;
  const up = input.pressed('ArrowUp') || (!walking && input.pressed('KeyW'));
  const down = input.pressed('ArrowDown') || (!walking && input.pressed('KeyS'));
  if (up) cmd.sailDelta = 1;
  if (down) cmd.sailDelta = -1;
  if (input.pressed('KeyQ')) cmd.firePort = true;
  if (input.pressed('KeyE')) cmd.fireStarboard = true;
  if (input.pressed('KeyT')) cmd.toggleTrim = true;
  if (input.pressed('KeyG')) cmd.cycleAmmo = true;
  if (input.pressed('KeyB')) cmd.board = true;
  if (input.pressed('KeyL')) cmd.toggleLights = true;
  if (input.pressed('KeyY')) {
    view.rig.cycleTarget(world);
    if (view.rig.mode !== 'battle') view.rig.setMode('battle', input);
  }
  cmd.trim = clamp(cmd.trim + input.axis(['KeyZ'], ['KeyX']) * dt * 0.8, -1, 1);
  for (const c of CAMERA_MODES) if (input.pressed(`Digit${c.key}`)) view.rig.setMode(c.id, input);
  if (input.pressed('KeyC')) {
    const i = CAMERA_MODES.findIndex((c) => c.id === mode);
    view.rig.setMode(CAMERA_MODES[(i + 1) % CAMERA_MODES.length]!.id as CameraMode, input);
  }
  if (input.pressed('BracketLeft')) world.day.setHours(world.day.hours - 1);
  if (input.pressed('BracketRight')) world.day.setHours(world.day.hours + 1);
  if (input.pressed('KeyN')) {
    const i = WEATHER_KINDS.indexOf(world.weather.kind);
    world.weather.set(WEATHER_KINDS[(i + 1) % WEATHER_KINDS.length]!, 20);
  }
  if (input.pressed('Tab')) {
    if (hud.toggleSettings()) input.releaseLock();
  }
  if (input.pressed('KeyH')) hud.toggleHelp();
  if (input.pressed('KeyV')) hud.toggleHud();
  if (input.pressed('KeyM')) audio.toggleMute();
}

const tmpV = new THREE.Vector3();

function step(dt: number, warmup = false): void {
  if (!warmup && started) handleKeys(dt);
  const alpha = world.update(dt, cmd);
  // Visual effects keep pace with the sim; the camera, controls and sound stay on real time.
  const simDt = dt * world.speed;
  const events = world.events.drain();
  view.syncShips(alpha);
  const cam = view.rig.camera;
  effects.handle(events, cam, view.drainModelEvents());
  hud.handle(events);
  view.rig.update(dt, world, view.ship, input);
  if (effects.shake > 0) view.rig.shake(effects.shake);
  effects.shake = 0;
  view.update(simDt);
  env.update(simDt, view.ship.position);
  const onShip = view.rig.mode === 'deck' || view.rig.mode === 'helm';
  effects.update(simDt, cam, onShip);
  view.render(dt);
  hud.tracker.update(dt, cam, (v) => view.modelOf(v)?.posed);
  if (audio.started) {
    const fwd = cam.getWorldDirection(tmpV).clone();
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion);
    audio.update(dt, world, { position: cam.position, forward: fwd, up }, events,
      (x, y, z) => new THREE.Vector3(x, y, z).applyMatrix4(view.ship.matrixWorld), onShip,
      () => env.randomGull());
  }
}

let last = performance.now();
function frame(now: number): void {
  const dt = Math.min(0.1, Math.max(0.001, (now - last) / 1000));
  last = now;
  step(dt);
  hud.update(view.rig.mode, view.rig.shotName, fpsAvg, renderScale);
  input.endFrame();

  // Dynamic resolution: keep the frame rate up by trading pixels.
  frameAvg += (dt - frameAvg) * 0.05;
  fpsAvg = 1 / frameAvg;
  scaleTimer += dt;
  if (scaleTimer > 1.5) {
    scaleTimer = 0;
    let next = renderScale;
    if (frameAvg > 1 / 45) next = Math.max(0.55, renderScale - 0.08);
    else if (frameAvg < 1 / 58 && renderScale < 1) next = Math.min(1, renderScale + 0.05);
    if (next !== renderScale) {
      renderScale = next;
      view.setRenderScale(renderScale);
      afterResize();
    }
  }
  requestAnimationFrame(frame);
}

/** ?weather=storm&hour=18.5&cam=cinematic&start - handy for sharing a scene. */
function applyUrlOptions(): void {
  const q = new URLSearchParams(location.search);
  const wx = q.get('weather');
  if (wx && (WEATHER_KINDS as readonly string[]).includes(wx)) {
    world.weather.dynamic = false;
    world.weather.set(wx as (typeof WEATHER_KINDS)[number], 0);
    world.wind.meanSpeed = world.weather.params.windSpeed;
    world.waves.settle({
      ...world.sea,
      windSpeed: world.weather.params.windSpeed,
      waveScale: world.weather.params.waveScale * world.settings.waveIntensity,
      swellHeight: world.weather.params.swellHeight,
      chop: world.weather.params.chop,
    });
  }
  const hour = Number(q.get('hour'));
  if (q.has('hour') && Number.isFinite(hour)) world.day.setHours(hour);
  const cam = q.get('cam');
  if (cam && CAMERA_MODES.some((c) => c.id === cam)) view.rig.setMode(cam as CameraMode, input);
  if (q.has('start')) (document.querySelector('.title-go') as HTMLButtonElement | null)?.click();
}

// Let the title card paint before the heavy start-up work.
setTimeout(() => void init(), 60);
(window as unknown as { __sim: unknown }).__sim = {
  world,
  get view() {
    return view;
  },
  /** Dev: run the game for a while without waiting on animation frames. */
  advance(seconds: number, dt = 1 / 30): void {
    for (let t = 0; t < seconds; t += dt) {
      step(dt);
      hud.update(view.rig.mode, view.rig.shotName, 1 / dt, renderScale);
      input.endFrame();
    }
  },
};
if (import.meta.env.DEV) (window as unknown as { __debug: unknown }).__debug = { THREE, FullscreenPass, NOISE3D_FRAG, weatherTuning };
